/**
 * POS Users - Operators management for Point of Sale
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, ArrowLeft, Plus, Edit2, Trash2, X,
  Shield, User, Mail, Phone, Key, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// User form modal
const UserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState(user || {
    name: '',
    email: '',
    phone: '',
    role: 'operator',
    pin: '',
  });
  const [showPin, setShowPin] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!user && (!formData.pin || formData.pin.length < 4)) {
      toast.error('El PIN debe tener al menos 4 dígitos');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">
            {user ? 'Editar Operador' : 'Nuevo Operador'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre completo"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="10 dígitos"
              />
            </div>
          </div>

          <div>
            <Label>Rol</Label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="operator">Operador</option>
              <option value="owner">Propietario</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {formData.role === 'owner' 
                ? 'Puede hacer reembolsos y ver reportes' 
                : 'Solo puede realizar ventas'}
            </p>
          </div>

          <div>
            <Label>{user ? 'Nuevo PIN (dejar vacío para mantener)' : 'PIN de acceso *'}</Label>
            <div className="relative">
              <Input
                type={showPin ? 'text' : 'password'}
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder={user ? '••••' : 'Mínimo 4 dígitos'}
                maxLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              El PIN se usa para iniciar sesión en el POS
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : user ? 'Actualizar' : 'Crear Operador'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default function POSUsers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await posAPI.getUsers();
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Error cargando operadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (formData) => {
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.pin) delete updateData.pin;
        await posAPI.updateUser(editingUser.id, updateData);
        toast.success('Operador actualizado');
      } else {
        await posAPI.createUser(formData);
        toast.success('Operador creado');
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando operador');
      throw err;
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Desactivar a "${user.name}"?`)) return;
    
    try {
      await posAPI.deleteUser(user.id);
      toast.success('Operador desactivado');
      fetchUsers();
    } catch (err) {
      toast.error('Error desactivando operador');
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'owner') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Propietario
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-secondary text-muted-foreground flex items-center gap-1">
        <User className="w-3 h-3" />
        Operador
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Operadores</h1>
              <p className="text-xs text-muted-foreground">{users.length} usuarios</p>
            </div>
          </div>
          
          <Button onClick={() => { setEditingUser(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        {/* Info Banner */}
        <div className="glass rounded-xl p-4 mb-6 flex items-start gap-3">
          <Key className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium">Acceso con PIN</p>
            <p className="text-sm text-muted-foreground">
              Los operadores usan su PIN para iniciar sesión en el POS. 
              Los propietarios pueden hacer reembolsos y ver reportes completos.
            </p>
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Cargando operadores...
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 glass rounded-xl">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">No hay operadores registrados</p>
            <Button onClick={() => { setEditingUser(null); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primer operador
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`glass rounded-xl p-4 ${!user.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      {getRoleBadge(user.role)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => { setEditingUser(user); setShowModal(true); }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDeleteUser(user)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1 text-sm text-muted-foreground">
                  {user.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span>{user.email}</span>
                    </div>
                  )}
                  {user.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    <span>PIN: ••••</span>
                  </div>
                </div>

                {!user.is_active && (
                  <p className="text-xs text-destructive mt-2">Usuario desactivado</p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <UserModal
            user={editingUser}
            onClose={() => { setShowModal(false); setEditingUser(null); }}
            onSave={handleSaveUser}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
